import { assertNever } from '@ofmos/shared/exhaustive';
import type { CheckpointSettings, FieldType } from '@ofmos/shared/field-types';
import type { FieldMetadata, SelectOptionDef } from '../data/client';

/** As much of `FieldOptions` as the width rule reads: the one setting that narrows a column. */
interface FieldOptionsLike {
  readonly checkpoint?: CheckpointSettings | undefined;
}

/**
 * INV-12 TYPE-LEVEL EXHAUSTIVENESS, UI side.
 *
 * The display/input dispatch maps are `Record<FieldType, ...>`, so a new field
 * type in `shared` breaks the typecheck until both maps grow an entry. This file
 * adds the second half: the switches that answer "how does this type BEHAVE in a
 * layout" all end in `assertNever`, so a new type also cannot silently inherit a
 * default that happens to compile.
 */

/** The layout family a type belongs to. Drives alignment, widths and the wizard. */
export type FieldShape =
  | 'text'
  | 'number'
  | 'boolean'
  | 'date'
  | 'option'
  | 'options'
  | 'relation'
  | 'money'
  /**
   * FILES (`file-attachments.md` D11, D18). Its own shape rather than `'options'`, which it
   * superficially resembles: a chip list of tags is a closed set the user picks from, and an
   * attachment list is an open set the user UPLOADS into. The five switches below answer
   * differently for each, and the union value is what makes them say so out loud.
   */
  | 'files';

export function fieldShape(type: FieldType): FieldShape {
  switch (type) {
    case 'TEXT':
    case 'EMAIL':
    case 'URL':
    case 'PHONE':
      return 'text';
    case 'NUMBER':
      return 'number';
    case 'BOOLEAN':
      return 'boolean';
    case 'DATE':
    case 'DATETIME':
      return 'date';
    case 'SELECT':
      return 'option';
    case 'MULTI_SELECT':
      return 'options';
    case 'RELATION':
      return 'relation';
    case 'CURRENCY':
      return 'money';
    case 'FILES':
      return 'files';
    default:
      return assertNever(type, 'fieldShape');
  }
}

/** Numbers and money right-align: a column of digits is read by its last digit. */
export function fieldAlign(type: FieldType): 'left' | 'right' {
  const shape = fieldShape(type);
  return shape === 'number' || shape === 'money' ? 'right' : 'left';
}

/**
 * A CHECKPOINT COLUMN IS FOUR REM (`docs/decisions/phase-b-coach.md` D5).
 *
 * Eleven rem is what a DATE column is worth when it holds "26 Jul 2026"; a checkpoint cell
 * holds a dot and "7 Sep". The seeded "Onboarding" view carries EIGHT of them, and the
 * difference between the two widths is 56 rem of horizontal scroll on a 1440 screen - which
 * is the whole verification this column exists for.
 */
const CHECKPOINT_COLUMN_WIDTH = 4;

/**
 * Column width hint for the dense table, in rem.
 *
 * The FIELD is optional so that the twelve callers that only hold a type keep working, and so
 * that "how wide is a DATE" stays answerable without one. Where the caller does hold the
 * field - the table's own column model - a per-field SETTING can narrow it.
 */
export function fieldColumnWidth(type: FieldType, settings?: FieldOptionsLike): number {
  if (settings?.checkpoint !== undefined && fieldShape(type) === 'date') {
    return CHECKPOINT_COLUMN_WIDTH;
  }
  switch (fieldShape(type)) {
    case 'boolean':
      return 7;
    case 'number':
      return 8;
    case 'money':
      return 9;
    case 'date':
      return 11;
    case 'option':
      return 10;
    case 'options':
      return 14;
    case 'relation':
      return 12;
    case 'text':
      return 14;
    /**
     * The `options` width, and for the same reason: a row of chips reads like a row of tags
     * (`file-attachments.md` D18). This switch has a `default` fall-through, so a missing arm
     * would silently give attachments the 12 rem a relation gets - narrow enough to truncate
     * the first filename, which is the one thing the cell exists to show.
     */
    case 'files':
      return 14;
    default:
      return 12;
  }
}

/**
 * Whether the create-field wizard must ask for per-type settings before the
 * field can exist (options for a SELECT, a target for a RELATION, ...).
 */
export function requiresTypeSettings(type: FieldType): boolean {
  const shape = fieldShape(type);
  /**
   * `'files'` is deliberately NOT here (`file-attachments.md` D11). A Files field with no
   * settings is a complete Files field - it holds up to `FILES_FIELD_DEFAULT_MAX`
   * attachments - unlike a SELECT with no options, which is a field nobody can put a value
   * in. The panel exists so an admin CAN change the cap, not because the field needs one.
   */
  return shape === 'option' || shape === 'options' || shape === 'relation';
}

export function selectOptionsOf(field: FieldMetadata): readonly SelectOptionDef[] {
  return [...(field.options?.selectOptions ?? [])].sort((a, b) => a.position - b.position);
}

export function optionById(field: FieldMetadata, id: string): SelectOptionDef | undefined {
  return field.options?.selectOptions?.find((option) => option.id === id);
}

/** Type labels for the admin UI. A map, so a new type is a compile error here too. */
export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  TEXT: 'Text',
  NUMBER: 'Number',
  BOOLEAN: 'Yes / No',
  DATE: 'Date',
  DATETIME: 'Date and time',
  SELECT: 'Select',
  MULTI_SELECT: 'Multi-select',
  RELATION: 'Relation',
  EMAIL: 'Email',
  PHONE: 'Phone',
  URL: 'Link',
  CURRENCY: 'Currency',
  FILES: 'Files',
};

export const FIELD_TYPE_HINTS: Record<FieldType, string> = {
  TEXT: 'Any single-line text.',
  NUMBER: 'A decimal number. Never use it for money.',
  BOOLEAN: 'True or false, rendered as Yes / No.',
  DATE: 'A calendar day without a time.',
  DATETIME: 'An instant, stored with its timezone offset.',
  SELECT: 'One option from a fixed list you define.',
  MULTI_SELECT: 'Any number of options from a fixed list.',
  RELATION: 'A link to records of another object.',
  EMAIL: 'A validated email address.',
  PHONE: 'Stored as E.164 plus the number as it was typed.',
  URL: 'A validated link.',
  CURRENCY: 'An exact amount plus its ISO currency code.',
  FILES:
    'Files fields hold attachments on a record. Each file can be up to 25 MB, and one field can hold up to 25 files.',
};

/**
 * IS THIS FIELD SAFE TO PUT IN A `select`? (record-page-and-views.md 1.2, D3.)
 *
 * A MANY_TO_MANY owns NO physical column, so `POST /api/query` answers HTTP 200 with
 * `null` for it on every row - no error, no warning, no log. Any surface that selects
 * one therefore renders an em dash forever, no matter how many records are actually
 * linked, which is the exact "plausible-looking empty answer" the module's third rule
 * exists to prevent. `agency.people` looked like that in every table and on every
 * record page before this module.
 *
 * The relationship is not lost by dropping the field: it is rendered by the related
 * SECTION, which reads the junction object and can therefore show the role as well as
 * the person.
 */
export function isSelectableField(field: FieldMetadata): boolean {
  return !(field.type === 'RELATION' && field.options?.relation?.kind === 'MANY_TO_MANY');
}
